import { useState } from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Link,
  Alert,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  useEventAgentRun,
  useSubmitEventAgentFeedback,
  AgentRunResult,
  FixMatchResult,
} from '../api/useEventAgent';

interface Props {
  runId: string | null;
  onClose: () => void;
}

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  queued: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const CLASSIFICATION: Record<
  string,
  { label: string; color: 'default' | 'warning' | 'error' }
> = {
  false_positive: { label: 'false positive', color: 'error' },
  mis_lined: { label: 'mis-lined', color: 'warning' },
  not_a_bug: { label: 'not a bug', color: 'default' },
};

/**
 * Right-side drawer that follows one event-agent run. Polls
 * GET /event-agent/runs/:id every 2.5s while in flight. Renders either the
 * map-event output contract (scrape → pair, with an interactive unmatched-
 * markets feedback section) or the fix-match verdict, depending on run.kind.
 */
export const EventAgentRunDrawer = ({ runId, onClose }: Props) => {
  const { data: run, isLoading } = useEventAgentRun(runId);
  const submitFeedback = useSubmitEventAgentFeedback();

  const [selectedPoly, setSelectedPoly] = useState<string | null>(null);
  const [selectedKalshi, setSelectedKalshi] = useState<string | null>(null);
  const [inverted, setInverted] = useState(false);
  // Markets the operator has paired via feedback this session — greyed out.
  const [resolved, setResolved] = useState<Set<string>>(new Set());

  const isFixMatch = run?.kind === 'fix-match';
  const mapResult =
    run && !isFixMatch ? (run.result as AgentRunResult | null) : null;
  const fixResult =
    run && isFixMatch ? (run.result as FixMatchResult | null) : null;
  const unmatchedPoly = mapResult?.unmatchedMarkets?.polymarket ?? [];
  const unmatchedKalshi = mapResult?.unmatchedMarkets?.kalshi ?? [];
  const progress = run?.progressLog ?? [];

  const handlePairSelected = async () => {
    if (!runId || !selectedPoly || !selectedKalshi) return;
    await submitFeedback.mutateAsync({
      runId,
      missedPairs: [
        {
          polymarketExternalId: selectedPoly,
          kalshiTicker: selectedKalshi,
          polarityInverted: inverted,
        },
      ],
    });
    setResolved((prev) =>
      new Set(prev).add(`p:${selectedPoly}`).add(`k:${selectedKalshi}`),
    );
    setSelectedPoly(null);
    setSelectedKalshi(null);
    setInverted(false);
  };

  return (
    <Drawer
      anchor="right"
      open={!!runId}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 2 } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" sx={{ color: 'common.white' }}>
          {isFixMatch ? 'Fix-Match Run' : 'Auto-Map Run'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider sx={{ my: 1 }} />

      {isLoading && !run && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {run && (
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              size="small"
              label={run.status}
              color={STATUS_COLOR[run.status] ?? 'default'}
            />
            {(run.status === 'queued' || run.status === 'running') && (
              <CircularProgress size={14} />
            )}
          </Stack>

          {isFixMatch ? (
            <>
              <Typography variant="body2">
                <strong>Pair:</strong>{' '}
                {run.fixMatchSnapshot?.pairName || run.pairId}
              </Typography>
              {run.reason && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Reason given:</strong> {run.reason}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2">
              <strong>Event:</strong> {run.eventQuery}
            </Typography>
          )}

          {run.error && <Alert severity="error">{run.error}</Alert>}

          {progress.length > 0 && (
            <Box>
              <Typography variant="subtitle2">
                Progress · {progress.length} event{progress.length === 1 ? '' : 's'}
              </Typography>
              <Box
                sx={{
                  maxHeight: 260,
                  overflowY: 'auto',
                  mt: 0.5,
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                }}
              >
                <List dense disablePadding>
                  {progress.map((p, i) => (
                    <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start', py: 0.25 }}>
                      <ListItemText
                        primary={`[${p.phase}] ${p.message}`}
                        secondary={
                          p.ts ? new Date(p.ts).toLocaleTimeString() : null
                        }
                        primaryTypographyProps={{ variant: 'caption' }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          sx: { fontSize: '0.6rem', opacity: 0.55 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}

          {/* ── fix-match verdict ── */}
          {fixResult && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2">Verdict</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {fixResult.classification && (
                    <Chip
                      size="small"
                      color={
                        CLASSIFICATION[fixResult.classification]?.color ??
                        'default'
                      }
                      label={
                        CLASSIFICATION[fixResult.classification]?.label ??
                        fixResult.classification
                      }
                    />
                  )}
                  <Chip
                    size="small"
                    color={
                      fixResult.action === 'pr_opened' ? 'success' : 'default'
                    }
                    label={
                      fixResult.action === 'pr_opened'
                        ? 'PR opened'
                        : 'no code change'
                    }
                  />
                </Stack>
              </Box>

              {fixResult.analysis && (
                <Box>
                  <Typography variant="subtitle2">Analysis</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fixResult.analysis}
                  </Typography>
                </Box>
              )}

              {fixResult.prUrl && (
                <Box>
                  <Typography variant="subtitle2">Draft PR</Typography>
                  <Link href={fixResult.prUrl} target="_blank" rel="noopener">
                    {fixResult.prUrl}
                  </Link>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Review before merging — the agent only opens drafts.
                  </Typography>
                </Box>
              )}

              {fixResult.correctedPair && (
                <Box>
                  <Typography variant="subtitle2">Corrected pair</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fixResult.correctedPair.polymarketExternalId} ↔{' '}
                    {fixResult.correctedPair.kalshiTicker}
                    {fixResult.correctedPair.pairId ? ' · created' : ''}
                  </Typography>
                </Box>
              )}

              {fixResult.warnings && fixResult.warnings.length > 0 && (
                <Alert severity="warning">
                  {fixResult.warnings.join(' · ')}
                </Alert>
              )}
            </>
          )}

          {/* ── map-event result ── */}
          {mapResult && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2">Summary</Typography>
                <Typography variant="body2" color="text.secondary">
                  {mapResult.cachedMarketsUpserted?.polymarket ?? 0} Polymarket +{' '}
                  {mapResult.cachedMarketsUpserted?.kalshi ?? 0} Kalshi markets
                  cached
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    color="success"
                    label={`${mapResult.pairsCreated?.length ?? 0} created`}
                  />
                  <Chip
                    size="small"
                    label={`${mapResult.pairsSkipped?.length ?? 0} already paired`}
                  />
                  <Chip
                    size="small"
                    color={
                      unmatchedPoly.length + unmatchedKalshi.length > 0
                        ? 'warning'
                        : 'default'
                    }
                    label={`${unmatchedPoly.length + unmatchedKalshi.length} unmatched`}
                  />
                </Stack>
              </Box>

              {mapResult.pairsCreated && mapResult.pairsCreated.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Pairs created</Typography>
                  <List dense disablePadding>
                    {mapResult.pairsCreated.map((p, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemText
                          primary={
                            p.name ||
                            `${p.polymarketExternalId} ↔ ${p.kalshiTicker}`
                          }
                          secondary={
                            (p.marketType ?? '') +
                            (p.polarityInverted ? ' · inverted' : '')
                          }
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {(unmatchedPoly.length > 0 || unmatchedKalshi.length > 0) && (
                <Box>
                  <Typography variant="subtitle2">
                    Unmatched markets
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The agent missed a pair? Select one Polymarket + one Kalshi
                    market and pair them.
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1 }}
                    alignItems="flex-start"
                  >
                    {/* Polymarket column */}
                    <List dense disablePadding sx={{ flex: 1 }}>
                      {unmatchedPoly.map((m) => {
                        const done = resolved.has(`p:${m.externalId}`);
                        return (
                          <ListItemButton
                            key={m.externalId}
                            dense
                            disabled={done}
                            selected={selectedPoly === m.externalId}
                            onClick={() => setSelectedPoly(m.externalId)}
                          >
                            <ListItemText
                              primary={`PM · ${m.question || m.externalId}`}
                              secondary={done ? 'paired ✓' : m.reason}
                              primaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                    {/* Kalshi column */}
                    <List dense disablePadding sx={{ flex: 1 }}>
                      {unmatchedKalshi.map((m) => {
                        const done = resolved.has(`k:${m.ticker}`);
                        return (
                          <ListItemButton
                            key={m.ticker}
                            dense
                            disabled={done}
                            selected={selectedKalshi === m.ticker}
                            onClick={() => setSelectedKalshi(m.ticker)}
                          >
                            <ListItemText
                              primary={`KX · ${m.title || m.ticker}`}
                              secondary={done ? 'paired ✓' : m.reason}
                              primaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={inverted}
                          onChange={(e) => setInverted(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          inverted polarity
                        </Typography>
                      }
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={
                        !selectedPoly ||
                        !selectedKalshi ||
                        submitFeedback.isLoading
                      }
                      onClick={handlePairSelected}
                    >
                      {submitFeedback.isLoading ? 'Pairing…' : 'Pair these'}
                    </Button>
                  </Stack>
                  {submitFeedback.isError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      Failed to create the pair — check both markets are cached.
                    </Alert>
                  )}
                </Box>
              )}

              {mapResult.gaps?.shapes && mapResult.gaps.shapes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">
                    Shape gaps (need a matching-core PR)
                  </Typography>
                  <List dense disablePadding>
                    {mapResult.gaps.shapes.map((s, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemText
                          primary={s.description}
                          secondary={
                            s.prUrl ? (
                              <Link
                                href={s.prUrl}
                                target="_blank"
                                rel="noopener"
                              >
                                {s.prUrl}
                              </Link>
                            ) : (
                              'no PR yet'
                            )
                          }
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {mapResult.warnings && mapResult.warnings.length > 0 && (
                <Alert severity="warning">
                  {mapResult.warnings.join(' · ')}
                </Alert>
              )}
            </>
          )}

          {run.feedback && run.feedback.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Operator corrections</Typography>
              <Typography variant="caption" color="text.secondary">
                {run.feedback.reduce(
                  (n, f) => n + (f.missedPairs?.length ?? 0),
                  0,
                )}{' '}
                missed pair(s) submitted
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </Drawer>
  );
};
